import React, { useMemo } from 'react';
import { buildColorMap, initials } from './colors';
import { EXCLUDED_HATCH_CLASS } from '../../../../../utils/excludedDays';

// The Predico ensemble, when present (owner/admin only), is ranked in as a
// fixed teal square so it reads as distinct from the per-forecaster colours.
const ENSEMBLE_COLOR = '#05788a'; // brand-600

// Hover text. The username (pseudonym) is shown to everyone; the real name and
// email are only present in the payload for admins (superusers), so they only
// appear in the tooltip for them, and only while blurring is off.
function cellTitle(row, bucketLabel, blurSensitive = false) {
  const score = Number(row.avg_score).toFixed(2);
  if (row.is_ensemble) {
    return `Rank ${row.rank} · ${bucketLabel} · Predico ensemble · ${score}`;
  }
  let identity = row.username || row.user_id;
  if (!blurSensitive) {
    if (row.name) identity += ` · ${row.name}`;
    if (row.email) identity += ` <${row.email}>`;
  }
  if (row.is_fixed_payment) identity += ' · contracted';
  return `Rank ${row.rank} · ${bucketLabel} · ${identity} · ${score}`;
}

/**
 * Leaderboard-over-time matrix (ported from the public About.js RankingTabs
 * idiom). Rows = rank position (top-N), columns = time buckets at the chosen
 * grain. Each cell is the forecaster who held that position in that bucket,
 * coloured per-forecaster so a single colour can be traced across a row.
 *
 * Rows are whatever display mode the parent chose (raw ranks, or the
 * ensemble/contracted-inclusive view): the ensemble renders as a teal ENS
 * square, and contracted forecaster rows render ringed to set them apart.
 * A bucket flagged `excluded` (an outage day) keeps its column, hatched.
 */
export default function LeaderboardMatrix({
  data,
  traceUserId = null,
  onTrace = null,
  topN = 10,
  blurSensitive = false,
  metricLabel = 'RMSE',
  colorMap: colorMapProp = null,
}) {
  const builtColorMap = useMemo(() => buildColorMap(data?.buckets || []), [data?.buckets]);
  const colorMap = colorMapProp || builtColorMap;
  const buckets = data?.buckets || [];

  if (!buckets.length) {
    return (
      <div className="p-8 text-center text-sm text-gray-500 dark:text-gray-400">
        No leaderboard data for this period.
      </div>
    );
  }

  const nCols = buckets.length;
  const traceActive = Boolean(traceUserId);
  const grain = data?.grain || 'day';

  // X-axis title. The per-column labels are bare numbers (day-of-month for the
  // daily grain, "Wnn" for weeks), so name what they are: for a daily window
  // that's the month they fall in, derived from the first bucket's ISO key.
  const firstKey = buckets[0]?.key || '';
  let xAxisTitle = 'Date';
  if (grain === 'day' && firstKey) {
    const dt = new Date(`${firstKey}T00:00:00`);
    if (!Number.isNaN(dt.getTime())) {
      xAxisTitle = dt.toLocaleString(undefined, { month: 'long', year: 'numeric' });
    }
  } else if (grain === 'week') {
    xAxisTitle = 'ISO week';
  } else if (grain === 'month') {
    xAxisTitle = 'Month';
  }

  const handleClick = (userId) => {
    if (!onTrace || !userId) return;
    onTrace(userId === traceUserId ? null : userId);
  };

  // The forecaster spotlighted from the filter bar, shown inline on the axis
  // row. Admins see the real email; everyone else the username pseudonym.
  const tracedRow = traceActive
    ? buckets.flatMap((b) => b.rows || []).find((r) => r.user_id === traceUserId)
    : null;
  const tracedLabel = tracedRow
    ? ((!blurSensitive && tracedRow.email) ? tracedRow.email : tracedRow.username)
    : null;
  const tracedColor = tracedRow ? (colorMap[traceUserId] || '#94a3b8') : null;

  return (
    <div className="flex gap-1.5">
      {/* Y-axis title: vertical "Rank" label flanking the rank-number column. */}
      <div className="flex items-center">
        <span className="[writing-mode:vertical-rl] rotate-180 text-[10px] uppercase tracking-wider font-mono text-slate-400 dark:text-gray-500">
          Rank
        </span>
      </div>
      <div className="flex-1 min-w-0">
      {/* pl-[30px] aligns the inline labels with the data cells: the 28px rank
          column + the 2px grid gap. */}
      <div className="flex items-center justify-between gap-2 mb-2 pl-[30px]">
        <span className="flex items-center gap-1.5 min-w-0">
          {tracedLabel && (
            <>
              <span
                className="h-2 w-2 rounded-full flex-shrink-0"
                style={{ backgroundColor: tracedColor }}
              />
              <span className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-gray-400 font-mono truncate">
                {tracedLabel}
              </span>
            </>
          )}
        </span>
        <span className="text-[10px] uppercase tracking-wider text-slate-400 dark:text-gray-500 font-mono flex-shrink-0">
          {metricLabel} rank · {data?.grain || 'day'}
        </span>
      </div>
      <div
        className="grid gap-[2px] saturate-[.88] brightness-[1.06] dark:saturate-[.8] dark:brightness-90"
        style={{
          gridTemplateColumns: `28px repeat(${nCols}, minmax(0, 1fr))`,
        }}
      >
        {Array.from({ length: topN }).map((_, r) => (
          <React.Fragment key={`row-${r}`}>
            <div className="flex items-center justify-end pr-2 text-[10px] font-mono text-slate-500 dark:text-gray-400 tabular-nums">
              {r + 1}
            </div>
            {buckets.map((b, c) => {
              if (b.excluded) {
                return (
                  <div
                    key={`c-${r}-${c}`}
                    title={`Day ${b.label}: Excluded · ${b.excluded.reason}`}
                    className={`h-7 sm:h-8 rounded-[2px] ${EXCLUDED_HATCH_CLASS}`}
                  />
                );
              }
              const row = (b.rows || [])[r];
              if (!row) {
                return (
                  <div
                    key={`c-${r}-${c}`}
                    className="h-7 sm:h-8 rounded-[2px] bg-slate-100/40 dark:bg-gray-800/30"
                  />
                );
              }
              const isEns = Boolean(row.is_ensemble);
              const isFixed = Boolean(row.is_fixed_payment);
              const color = isEns ? ENSEMBLE_COLOR : (colorMap[row.user_id] || '#94a3b8');
              // The ensemble is not a forecaster, so it never dims under a trace
              // and is not click-to-trace.
              const dim = traceActive && !isEns && row.user_id !== traceUserId;
              // Admins (real name present in payload) see real-name initials;
              // when blurred, or for anyone without a real name, fall back to
              // the username pseudonym's initials.
              const cellLabel = isEns
                ? 'ENS'
                : (!blurSensitive && row.name
                  ? initials(row.name)
                  : initials(row.username));
              return (
                <button
                  type="button"
                  key={`c-${r}-${c}`}
                  onClick={() => (isEns ? null : handleClick(row.user_id))}
                  title={cellTitle(row, b.label, blurSensitive)}
                  className={`flex items-center justify-center h-7 sm:h-8 rounded-[2px] text-[11px] font-mono font-semibold tracking-wide text-white transition ${
                    isEns ? 'cursor-default' : ''
                  } ${dim ? 'opacity-20 saturate-[.75]' : ''} ${isFixed ? 'ring-2 ring-inset ring-white/70 dark:ring-white/40' : ''}`}
                  style={{ backgroundColor: color }}
                >
                  <span className={isEns ? 'text-white font-bold' : ''}>
                    {cellLabel}
                  </span>
                </button>
              );
            })}
          </React.Fragment>
        ))}

        {/* bottom axis labels */}
        <div />
        {buckets.map((b, c) => (
          <div
            key={`lab-${c}`}
            className={`text-[10px] uppercase tracking-wider font-mono text-center pt-1.5 truncate ${
              b.excluded
                ? 'text-slate-300 dark:text-gray-600'
                : 'text-slate-500 dark:text-gray-400'}`}
          >
            {b.label}
          </div>
        ))}
      </div>
      {/* X-axis title, aligned to the data cells past the 28px rank column. */}
      <div className="pl-[30px] mt-1.5 text-center text-[10px] uppercase tracking-wider font-mono text-slate-400 dark:text-gray-500">
        {xAxisTitle}
      </div>
      </div>
    </div>
  );
}
